/**
 * PROPRIETARY NOTICE
 * THE ONESCENT DIAL - Core API Service
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 * 
 * PURPOSE:
 * Centralized API communication layer with:
 * - Consistent request/response handling
 * - Automatic auth token injection
 * - Error handling and retry logic
 * - Response body single-read guarantee
 * 
 * This service PREVENTS the "body stream already read" error by:
 * 1. Reading response body exactly once
 * 2. Providing consistent error format
 * 3. Handling all edge cases (empty body, non-JSON, network errors)
 */

import { authLogger } from '../utils/logger';

const API_BASE = '/api';

// Token storage key
const TOKEN_KEY = 'tod_auth_token';
const SESSION_ID_KEY = 'tod_session_id';
const REMEMBERED_EMAIL_KEY = 'tod_remembered_email';

/**
 * API Response wrapper - ensures consistent handling
 */
class ApiResponse {
  constructor(ok, status, data, error = null) {
    this.ok = ok;
    this.status = status;
    this.data = data;
    this.error = error;
  }
  
  static success(status, data) {
    return new ApiResponse(true, status, data, null);
  }
  
  static failure(status, error, data = null) {
    return new ApiResponse(false, status, data, error);
  }
}

/**
 * Core API Service
 * All API calls should go through this service
 */
class ApiService {
  constructor() {
    // Ensure single instance
    if (ApiService.instance) {
      return ApiService.instance;
    }
    ApiService.instance = this;
    
    // Initialize session ID for anonymous users
    this.ensureSessionId();
  }
  
  /**
   * Get or create session ID for anonymous history tracking
   */
  ensureSessionId() {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = `anon_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  }
  
  /**
   * Get stored auth token
   */
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }
  
  /**
   * Set auth token
   */
  setToken(token) {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      authLogger.log('Token stored:', token.substring(0, 20) + '...');
    } else {
      localStorage.removeItem(TOKEN_KEY);
      authLogger.log('Token cleared');
    }
  }
  
  /**
   * Check if user is authenticated (has valid token)
   */
  hasToken() {
    return !!this.getToken();
  }
  
  /**
   * Get remembered email
   */
  getRememberedEmail() {
    return localStorage.getItem(REMEMBERED_EMAIL_KEY);
  }
  
  /**
   * Set remembered email
   */
  setRememberedEmail(email) {
    if (email) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
  }
  
  /**
   * Build request headers
   */
  buildHeaders(includeAuth = true, customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-ID': this.ensureSessionId(),
      ...customHeaders,
    };
    
    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    return headers;
  }
  
  /**
   * Safely parse response body - READ ONCE ONLY
   * This is the key to preventing "body stream already read" errors
   */
  async parseResponse(response) {
    try {
      // Get the response text first (reads body once)
      const text = await response.text();
      
      // Try to parse as JSON
      if (text) {
        try {
          return JSON.parse(text);
        } catch (parseError) {
          // Not JSON - return as text wrapped in object
          return { message: text };
        }
      }
      
      // Empty body
      return null;
    } catch (readError) {
      authLogger.error('Failed to read response body:', readError);
      return null;
    }
  }
  
  /**
   * Make API request with consistent error handling
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint (without /api prefix)
   * @param {Object} options - Request options
   * @returns {ApiResponse}
   */
  async request(method, endpoint, options = {}) {
    const {
      body = null,
      includeAuth = true,
      headers: customHeaders = {},
      timeout = 30000,
      retries = 0,
    } = options;
    
    const url = `${API_BASE}${endpoint}`;
    const headers = this.buildHeaders(includeAuth, customHeaders);
    
    const fetchOptions = {
      method,
      headers,
    };
    
    // Add body for non-GET requests
    if (body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(body);
    }
    
    // Attempt request with optional retries
    let lastError = null;
    const maxAttempts = retries + 1;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        fetchOptions.signal = controller.signal;
        
        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
        
        // Parse response body ONCE
        const data = await this.parseResponse(response);
        
        if (response.ok) {
          return ApiResponse.success(response.status, data);
        }
        
        // Handle specific error statuses
        const errorMessage = data?.detail || data?.message || `Request failed with status ${response.status}`;
        
        // Special handling for auth errors
        if (response.status === 401 || response.status === 403) {
          // Token might be expired - don't retry
          authLogger.warn(`Auth error on ${endpoint}:`, response.status);
          return ApiResponse.failure(response.status, errorMessage, data);
        }
        
        // Rate limiting
        if (response.status === 429) {
          return ApiResponse.failure(response.status, 'Too many requests. Please wait a moment.', data);
        }
        
        // Server errors - might be worth retrying
        if (response.status >= 500 && attempt < maxAttempts) {
          lastError = errorMessage;
          await this.delay(1000 * attempt); // Exponential backoff
          continue;
        }
        
        return ApiResponse.failure(response.status, errorMessage, data);
        
      } catch (error) {
        // Network or timeout error
        if (error.name === 'AbortError') {
          lastError = 'Request timed out';
        } else {
          lastError = error.message || 'Network error';
        }
        
        authLogger.error(`Request failed (attempt ${attempt}/${maxAttempts}):`, lastError);
        
        if (attempt < maxAttempts) {
          await this.delay(1000 * attempt);
          continue;
        }
      }
    }
    
    return ApiResponse.failure(0, lastError || 'Request failed');
  }
  
  /**
   * Delay helper
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  
  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request('GET', endpoint, options);
  }
  
  async post(endpoint, body, options = {}) {
    return this.request('POST', endpoint, { ...options, body });
  }
  
  async put(endpoint, body, options = {}) {
    return this.request('PUT', endpoint, { ...options, body });
  }
  
  async delete(endpoint, options = {}) {
    return this.request('DELETE', endpoint, options);
  }
}

// Export singleton instance
const apiService = new ApiService();

export { ApiResponse };
export default apiService;
